import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import StoreIcon from '@mui/icons-material/Store';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useManagementAuth } from '../../auth/ManagementAuthContext';
import api from '../../services/api';
import { StoreBrief, ManagementRequestResponse, StoreClosingResponse } from '../../types';

const CompanyStoresPage: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { managementToken } = useManagementAuth();
  const [stores, setStores] = useState<StoreBrief[]>([]);
  const [storeRequests, setStoreRequests] = useState<ManagementRequestResponse[]>([]);
  const [closingSchedules, setClosingSchedules] = useState<StoreClosingResponse[]>([]);
  const [loading, setLoading] = useState(true);

  // 店舗追加ダイアログ
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addStoreName, setAddStoreName] = useState('');
  const [addStoreAddress, setAddStoreAddress] = useState('');
  const [addStorePhone, setAddStorePhone] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  // 解約確認ダイアログ
  const [closingDialogOpen, setClosingDialogOpen] = useState(false);
  const [closingStoreId, setClosingStoreId] = useState<string | null>(null);
  const [closingStoreName, setClosingStoreName] = useState('');
  const [closingReason, setClosingReason] = useState('');
  const [closingLoading, setClosingLoading] = useState(false);
  const [closingError, setClosingError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!managementToken || !companyId) return;
    try {
      const [storesData, requestsData, schedulesData] = await Promise.all([
        api.managementCompany.getStores(companyId, managementToken),
        api.managementCompany.getStoreRequests(companyId, managementToken).catch(() => []),
        api.managementCompany.getClosingSchedules(companyId, managementToken).catch(() => []),
      ]);
      setStores(storesData);
      setStoreRequests(requestsData);
      setClosingSchedules(schedulesData);
    } catch (error) {
      console.error('店舗一覧の取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  }, [managementToken, companyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getClosingSchedule = (storeId: string) =>
    closingSchedules.find((s) => s.store_id === storeId);

  const handleAddStore = async () => {
    if (!managementToken || !companyId) return;
    if (!addStoreName.trim()) {
      setAddError('店舗名を入力してください。');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await api.managementCompany.requestStoreAddition(companyId, managementToken, {
        request_type: 'store_addition',
        store_name: addStoreName,
        store_address: addStoreAddress,
        store_phone: addStorePhone,
      });
      setAddDialogOpen(false);
      setAddStoreName('');
      setAddStoreAddress('');
      setAddStorePhone('');
      await fetchData();
    } catch (err: any) {
      setAddError('店舗追加申請に失敗しました。');
    } finally {
      setAddLoading(false);
    }
  };

  const handleScheduleClosing = async () => {
    if (!managementToken || !companyId || !closingStoreId) return;
    setClosingLoading(true);
    setClosingError(null);
    try {
      await api.managementCompany.scheduleStoreClosing(
        companyId,
        closingStoreId,
        managementToken,
        closingReason ? { reason: closingReason } : undefined,
      );
      setClosingDialogOpen(false);
      setClosingStoreId(null);
      setClosingReason('');
      await fetchData();
    } catch (err: any) {
      setClosingError('解約予約に失敗しました。');
    } finally {
      setClosingLoading(false);
    }
  };

  const handleCancelClosing = async (storeId: string) => {
    if (!managementToken || !companyId) return;
    try {
      await api.managementCompany.cancelStoreClosing(companyId, storeId, managementToken);
      await fetchData();
    } catch (error) {
      console.error('解約予約の取り消しに失敗しました', error);
    }
  };

  const openClosingDialog = (storeId: string, storeName: string) => {
    setClosingStoreId(storeId);
    setClosingStoreName(storeName);
    setClosingReason('');
    setClosingError(null);
    setClosingDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const pendingRequests = storeRequests.filter((r) => r.status === 'pending');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="text.primary">
          店舗一覧
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setAddError(null);
            setAddDialogOpen(true);
          }}
          sx={{ bgcolor: '#3E4298', '&:hover': { bgcolor: '#4b49d6' } }}
        >
          店舗を追加
        </Button>
      </Box>

      {/* 承認待ち店舗申請 */}
      {pendingRequests.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HourglassEmptyIcon sx={{ fontSize: 18 }} />
              承認待ちの店舗追加申請
            </Typography>
            {pendingRequests.map((req) => (
              <Box key={req.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <StoreIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                <Typography variant="body2">{req.store_name}</Typography>
                <Chip label="承認待ち" size="small" color="warning" />
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ p: 0 }}>
          <List disablePadding>
            {stores.map((store, index) => {
              const closingSchedule = getClosingSchedule(store.id);
              return (
                <ListItem
                  key={store.id}
                  disablePadding
                  divider={index < stores.length - 1}
                  secondaryAction={
                    closingSchedule ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={<EventBusyIcon />}
                          label={`解約予約: ${closingSchedule.closing_scheduled_at}`}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelClosing(store.id);
                          }}
                        >
                          取消
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          openClosingDialog(store.id, store.name || '（店舗名未設定）');
                        }}
                      >
                        解約予約
                      </Button>
                    )
                  }
                >
                  <ListItemButton
                    onClick={() => navigate(`/management/company/${companyId}/store/${store.id}`)}
                    sx={{ pr: closingSchedule ? 28 : 12 }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <StoreIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={store.name || '（店舗名未設定）'}
                      secondary={store.address || ''}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {stores.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="登録店舗がありません"
                  sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}
                />
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>

      {/* 店舗追加ダイアログ */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>店舗追加申請</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            追加する店舗の情報を入力してください。運営の承認後に利用可能になります。
          </Typography>
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addError}
            </Alert>
          )}
          <TextField
            label="店舗名"
            value={addStoreName}
            onChange={(e) => setAddStoreName(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={addLoading}
            placeholder="例: ○○店"
          />
          <TextField
            label="店舗住所"
            value={addStoreAddress}
            onChange={(e) => setAddStoreAddress(e.target.value)}
            fullWidth
            margin="normal"
            disabled={addLoading}
            placeholder="例: 東京都渋谷区..."
          />
          <TextField
            label="電話番号"
            value={addStorePhone}
            onChange={(e) => setAddStorePhone(e.target.value)}
            fullWidth
            margin="normal"
            disabled={addLoading}
            placeholder="例: 03-1234-5678"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>
            キャンセル
          </Button>
          <Button
            onClick={handleAddStore}
            variant="contained"
            disabled={addLoading}
            sx={{ bgcolor: '#3E4298', '&:hover': { bgcolor: '#4b49d6' } }}
          >
            {addLoading ? <CircularProgress size={20} /> : '申請する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 解約確認ダイアログ */}
      <Dialog open={closingDialogOpen} onClose={() => setClosingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>店舗の解約予約</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            「{closingStoreName}」の解約を予約します。解約は当月末に実行されます。
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            解約予約後も月末までは通常通りご利用いただけます。解約予約はいつでも取り消し可能です。
          </Typography>
          {closingError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {closingError}
            </Alert>
          )}
          <TextField
            label="解約理由（任意）"
            value={closingReason}
            onChange={(e) => setClosingReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            disabled={closingLoading}
            placeholder="理由をご記入ください（任意）"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClosingDialogOpen(false)} disabled={closingLoading}>
            キャンセル
          </Button>
          <Button
            onClick={handleScheduleClosing}
            variant="contained"
            color="error"
            disabled={closingLoading}
          >
            {closingLoading ? <CircularProgress size={20} /> : '解約を予約する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyStoresPage;
